import { Acquisition } from "./acquisition";
import { BuildingAcquisitionInfo } from "./building-acquisition-info";
import { BuildingInspectionInfo } from "./building-inspection";
import { BuildingPlansInfo } from "./building-plans-info";
import { ClimatisationInfo } from "./climatisation";
import { Cluster } from "./cluster";
import { ElectricityInfoDto } from "./electricity";
import { EnergyPerformanceInfo } from "./energy-performance";
import { FireSecurityBuildingInfoDto } from "./fire-security";
import { HistoryLogType } from "./history-log";
import { BuildingOwnerShipInfo } from "./ownership-info";
import { AvailableRenovationLabel } from "./renovation";
import { Resource } from "./resource";
import { UnitDTO } from "./unit";
import { WaterBuildingInfoDto } from "./water-info";

export enum PermitStatus {
  NietVergund = "niet vergund",
  StedenbouwkundigeVergunningVerkregen = "stedenbouwkundige vergunning verkregen",
  VergundGeacht = "vergund geacht",
  StedenbouwkundigeInbreukGekend = "stedenbouwkundige inbreuk gekend",
}

export enum BuildingType {
  Opbrengsteigendom = "Opbrengsteigendom",
  Woning = "Woning",
  Retail = "Retail",
}

export enum HeritageStatus {
  GeenErfgoed = "geen erfgoed",
  GeïnventariseerdErfgoed = "geïnventariseerd erfgoed",
  VastgesteldErfgoed = "vastgesteld erfgoed",
  BeschermMonument = "bescherm monument",
  BeschermdLandschap = "beschermd landschap",
  BeschermdStadsOfDorpsgezicht = "beschermd - stads- of dorpsgzicht",
  BeschermdeArcheologischeSite = "beschermde archeologische site",
}

export enum ConstructiveMainMaterial {
  Beton = "Beton",
  Hout = "Hout",
}

export enum DayLightPassThrough {
  RamenVoorEnAchter = "Ramen voor- en achteraan",
  RamenAlleenVooraan = "Ramen alleen vooraan",
}

export enum SewageType {
  GescheidenRiolering = "Riolering gescheiden",
  NietGescheidenRiolering = "Riolering niet gescheiden",
}

export enum BreeamRating {
  Outstanding = "Outstanding",
  Excellent = "Excellent",
  VeryGood = "Very Good",
  Good = "Good",
  Pass = "Pass",
  Unclassified = "Unclassified",
}

export enum BreeamMethod {
  NewConstruction = "New Construction",
  InUse = "In-Use",
  RefurbishmentAndFitOut = "Refurbishment & Fit-Out",
  Communities = "Communities",
}

export enum WellRating {
  Certified = "Certified",
  Bronze = "Bronze",
  Silver = "Silver",
  Gold = "Gold",
  Platinum = "Platinum",
  Unclassified = "Unclassified",
}

export enum WellMethod {
  WellBuildingStandardV2 = "WELL Building Standard v2",
  WellBuildingStandardV1 = "WELL Building Standard v1",
  WellCoreV2 = "WELL Core v2",
  WellCommunityStandard = "WELL Community Standard",
  WellHealthSafetyRating = "WELL Health-Safety Rating",
}

export enum StrategicStatus {
  Afstoten = "Afstoten",
  FlexibelBehouden = "Flexibel Behouden",
  StrategischBehouden = "Strategisch Behouden",
  BijzonderBehouden = "Bijzonder behouden",
}

export enum OwnershipType {
  Huur = "Huur",
  Eigendom = "Eigendom",
  InErfpachtGegeven = "In erfpacht gegeven",
  Gebruiksovereenkomst = "Gebruiksovereenkomst",
  InConcessieGegeven = "In concessie gegeven",
  LongLeaseHold = "Long lease hold",
  Verkocht = "Verkocht",
}

export type BuildingDTO = {
  id?: string | null;
  tempId?: string | null;
  avatar?: any;
  location?: any;
  city?: string;
  street?: string;
  climatisationInfo?: ClimatisationInfo;
  buildingOwnershipInfo?: BuildingOwnerShipInfo;
  buildingAcquisitionInfo?: BuildingAcquisitionInfo;
  resourceId?: string;
  energyPerformanceInfo?: EnergyPerformanceInfo;
  acquisitionFile?: Acquisition;
  electricityInfo?: ElectricityInfoDto;
  fireSecurityBuildingInfo?: FireSecurityBuildingInfoDto;
  province?: string;
  zip?: string;
  region?: string;
  googlePlaceLongAddress?: string;
  placeId?: string;
  housenr?: string;
  units?: UnitDTO[];
  grossActualRentalIncomePerYear?: number;
  historyLog?: HistoryLogType[];
  waterBuildingInfo?: WaterBuildingInfoDto;
  closingFiles?: any[];
  owner?: boolean;
  company?: any;
  buildingType?: BuildingType;
  heritageStatus?: HeritageStatus;
  daylightPassthrough?: DayLightPassThrough;
  constructionYear?: string;
  amountOfFloors?: string;
  nettoSurfaceInclCommonAreas?: string;
  nettoSurfaceExclCommonAreas?: string;
  numBicycleSheds?: string;
  numParkingsInside?: string;
  numParkingsOutside?: string;
  plotSurface?: string;
  plotDepth?: string;
  plotWidth?: string;
  totalCashOut?: string;
  acquisitionDate?: Date;
  constructiveMainMaterial?: ConstructiveMainMaterial;
  sewageType?: SewageType;
  purchasedAsProfessionalBuyer?: boolean;
  backPayments?: string;
  deadlineBackPayments?: Date;
  parkingPlaceNearBuilding?: string;
  mobiscore?: string;
  sharePointFolderId?: string;
  sharePointPicturesFolderId?: string;
  sharePointPicturesFolderUrl?: string;
  sharePointFolderUrl?: string;
  sharePointAttributenFolderId?: string;
  sharePointAttributenFolderUrl?: string;
  permitStatus?: PermitStatus;
  layoutPermitStatus?: PermitStatus;
  buildingInspectionInfo?: BuildingInspectionInfo;
  buildingAlreadyRenovated?: AvailableRenovationLabel[];
  buildingToRenovate?: AvailableRenovationLabel[];
  buildingPlansInfo?: BuildingPlansInfo;
  conformityAttest?: boolean;
  numUnitsWithPermit?: string;
  numUnitsWithoutPermit?: string;
  buildingServices?: any[];
  energyMeters?: any[];
  roofs?: any[];
  windows?: any[];
  facades?: any[];
  commonAreas?: any[];
  seller?: Resource;
  broker?: Resource;
  notary?: Resource;
  techDD?: Resource;
  legalDD?: Resource;
  energyAudit?: Resource;
  renovations?: any[];
  pictures?: any[];
  ticketCount?: number;
  totalCashOutIncludingRenovation?: number;
  breeamRating?: BreeamRating;
  breeamMethod?: BreeamMethod;
  breeamScorePercent?: number;
  breeamVersion?: string;
  breeamCertificateId?: string;
  wellRating?: WellRating;
  wellMethod?: WellMethod;
  wellScorePoints?: number;
  wellCertificateId?: string;
  wellValidFrom?: Date;
  wellValidTo?: Date;
  strategicStatus?: StrategicStatus;
  ownershipType?: OwnershipType;
  /**
   * ManyToMany relationship - a building can belong to multiple clusters
   */
  clusters?: Cluster[];
  /**
   * @deprecated Legacy field maintained for backward compatibility during migration.
   * Use `clusters` array instead.
   */
  cluster?: Cluster | null;
};
